import React, { useEffect, useState } from "react";
import { collection, getDocs, limit, orderBy, query, doc, getDoc } from "firebase/firestore";
import { toast } from "react-hot-toast";
import { db } from "../../lib/firebase";
import { useAuth } from "../../context/AuthProvider";
import {
  BroadcastSettings,
  BroadcastSourceType,
  getBroadcastSettings,
  updateBroadcastSettings,
} from "../../services/whatsappBroadcast.service";
import { WhatsAppBroadcastModal } from "../whatsapp/WhatsAppBroadcastModal";

type Row = { sourceType: BroadcastSourceType; sourceId: string; label: string; sub: string; active: boolean };

const kolkataDateId = (d = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);

const tsMs = (v: any) => (v?.toMillis ? v.toMillis() : v?.seconds ? v.seconds * 1000 : v ? new Date(v).getTime() : 0);

export const WhatsAppBroadcastPanel: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<BroadcastSettings | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [open, setOpen] = useState<Row | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setSettings(await getBroadcastSettings());
        const now = Date.now();
        const out: Row[] = [];
        const specials = await getDocs(query(collection(db, "special_announcements"), orderBy("createdAt", "desc"), limit(15)));
        specials.forEach((s) => {
          const d: any = s.data();
          const exp = tsMs(d.expiresAt);
          out.push({
            sourceType: "special",
            sourceId: s.id,
            label: `${d.kitchenName || d.kitchenId || "Kitchen"} · ${d.name || d.title || s.id}`,
            sub: `Special${d.specialDate ? ` · ${d.specialDate}` : ""}${d.price ? ` · ₹${d.price}` : ""}`,
            active: d.active !== false && (!exp || exp > now),
          });
        });
        const notices = await getDocs(query(collection(db, "notices"), orderBy("createdAt", "desc"), limit(10)));
        notices.forEach((n) => {
          const d: any = n.data();
          if (!d.title && !d.message) return;
          const exp = tsMs(d.expiresAt);
          out.push({
            sourceType: "notice",
            sourceId: n.id,
            label: d.title || n.id,
            sub: `Notice · ${d.type || ""}`,
            active: d.active !== false && (!exp || exp > now),
          });
        });
        for (const dateId of [kolkataDateId(), kolkataDateId(new Date(now + 86400000))]) {
          const m = await getDoc(doc(db, "admin_daily_menu", dateId));
          if (!m.exists()) continue;
          const km = (m.data() as any).kitchenMenus || {};
          const n = Object.values(km).reduce((a: number, k: any) => a + ((k?.items || []).length || 0), 0);
          out.push({ sourceType: "daily_menu", sourceId: dateId, label: `Daily menu ${dateId}`, sub: `${Object.keys(km).length} kitchens · ${n} items`, active: n > 0 });
        }
        setRows(out);
      } catch (e: any) {
        toast.error(e?.message || "Failed to load announcements");
      }
    })();
  }, []);

  const toggle = async (key: keyof BroadcastSettings) => {
    if (!settings || !user) return;
    const next = !settings[key];
    const msg =
      key === "enabled"
        ? next
          ? "Turn ON WhatsApp broadcasts to customers? Live sends (button, and auto-send if enabled) will start messaging subscribed customers."
          : "Turn OFF WhatsApp broadcasts?"
        : next
        ? "Enable AUTO-SEND? New/activated kitchen specials will be sent to subscribers automatically (only while the main switch is ON)."
        : "Disable auto-send?";
    if (!window.confirm(msg)) return;
    try {
      await updateBroadcastSettings({ [key]: next }, user.uid);
      setSettings({ ...settings, [key]: next });
      toast.success("Saved");
    } catch (e: any) {
      toast.error(e?.message || "Failed to save");
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 mt-4">
      <h3 className="text-lg font-bold text-gray-800">📣 WhatsApp broadcast (customers + Channel)</h3>
      <p className="text-xs text-gray-500 mb-3">
        Sends kitchen specials as an approved WhatsApp marketing template to customers who opted in or have an active tiffin
        subscription. Opted-out numbers (STOP) are never messaged.
      </p>

      {settings && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => toggle("enabled")}
            className={`px-3 py-2 rounded-lg text-sm font-bold border ${settings.enabled ? "bg-green-600 text-white border-green-600" : "bg-gray-100 text-gray-700"}`}
          >
            Broadcast switch: {settings.enabled ? "ON" : "OFF"}
          </button>
          <button
            onClick={() => toggle("autoSend")}
            className={`px-3 py-2 rounded-lg text-sm font-bold border ${settings.autoSend ? "bg-green-600 text-white border-green-600" : "bg-gray-100 text-gray-700"}`}
          >
            Auto-send new specials: {settings.autoSend ? "ON" : "OFF"}
          </button>
        </div>
      )}

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-xs text-gray-400">No announcements found.</p>}
        {rows.map((r) => (
          <div key={`${r.sourceType}:${r.sourceId}`} className="flex items-center justify-between gap-3 p-3 border rounded-lg">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{r.label}</p>
              <p className="text-[11px] text-gray-500">
                {r.sub} · {r.active ? <span className="text-green-700">active</span> : <span className="text-gray-400">inactive</span>}
              </p>
            </div>
            <button
              onClick={() => setOpen(r)}
              className="shrink-0 px-3 py-2 rounded-lg text-xs font-bold bg-green-600 text-white hover:bg-green-700"
            >
              Send to WhatsApp
            </button>
          </div>
        ))}
      </div>

      {open && (
        <WhatsAppBroadcastModal
          sourceType={open.sourceType}
          sourceId={open.sourceId}
          label={open.label}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
};

export default WhatsAppBroadcastPanel;
